<template>
  <Form method="post" @submit="handleSubmit">
    <h1 class="text-center text-[2.5rem] leading-12 font-bold">
      {{ t('shared-link.get-access') }}
    </h1>
    <p class="mt-2 text-center font-semibold text-on-surface-secondary">
      {{ t('shared-link.get-access-description') }}
    </p>

    <TextInput
      v-model="email"
      class="mt-6"
      :label="t('auth.email')"
      :placeholder="t('shared-link.email-placeholder')"
      :rules="[isRequired, isEmail]"
      data-testid="get-access-email"
      @update:model-value="errorMessage = ''"
    />

    <Alert v-if="errorMessage" class="mt-4" :type="AlertType.ERROR" icon>
      {{ errorMessage }}
    </Alert>

    <Button
      :label="t('shared-link.request-access')"
      class="mt-6 w-full"
      type="submit"
      data-testid="get-access-submit"
    />

    <i18n-t
      keypath="shared-link.have-account"
      tag="p"
      class="mt-6 text-center text-on-surface-secondary"
    >
      <template #link>
        <button
          type="button"
          class="text-primary underline"
          data-testid="get-access-sign-in"
          @click="emit('change-type', SelfRegisterType.SIGN_IN)"
        >
          {{ t('shared-link.here') }}
        </button>
      </template>
    </i18n-t>
  </Form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Form } from 'vee-validate';
import { Alert, AlertType, Button } from '@exfluential/ui';
import {
  apiCheckEmail,
  apiResendInvitation,
  EmailStatus,
  SelfRegisterType,
} from '~/api/auth';
import { TextInput } from '~/components/inputs/text-input';
import { useLoader } from '~/composables/use-loader';
import {
  emailStatusLoginStepMap,
  type LoginStep,
} from '~/pages/auth/login/login.constants';
import { isEmailI18n, isRequiredI18n } from '~/utils/validators';

const email = defineModel<string>({ required: true });

const emit = defineEmits<{
  'change-type': [value: LoginStep];
}>();

const { t } = useI18n();

const isRequired = isRequiredI18n(t);
const isEmail = isEmailI18n(t);

const errorMessage = ref('');

const handleSubmit = async () => {
  await useLoader({
    action: async () => {
      const { emailStatus } = await apiCheckEmail({ email: email.value });

      if (emailStatus === EmailStatus.REGISTERED) {
        emit('change-type', SelfRegisterType.SIGN_IN);
        return;
      }
      if (emailStatus === EmailStatus.PENDING) {
        await apiResendInvitation({ email: email.value });
      }
      emit('change-type', emailStatusLoginStepMap[emailStatus]);
    },
    onError: (err: unknown) => {
      errorMessage.value =
        (err as { data?: { message?: string } })?.data?.message ??
        t('auth.auth-failed');
    },
  });
};
</script>
